import React, {
    Fragment,
    useRef,
    useState,
    useCallback,
    useEffect,
} from 'react';
import { translate } from '../Services/translation';

let menuIdCounter = 0;

const DESKTOP_MEDIA_QUERY = '(min-width: 641px)';

const stripHtml = (html) => html?.replace(/<[^>]*>/g, '').trim() || '';

const focusFirstMenuItem = (menuElement) => {
    menuElement
        ?.querySelector(
            'li.navbar__item a.navbar__link, li.navbar__item button.navbar__icon--open'
        )
        ?.focus();
};

const closeAllNavbarMenus = (navbar) => {
    navbar?.querySelectorAll('.navbar__menu--show').forEach((element) => {
        element.classList.remove('navbar__menu--show');
    });
    navbar?.querySelectorAll('.navbar__menu--behind').forEach((element) => {
        element.classList.remove('navbar__menu--behind');
    });
    navbar?.querySelectorAll('.navbar__menu--current').forEach((element) => {
        element.classList.remove('navbar__menu--current');
    });
    navbar
        ?.querySelectorAll('.navbar__item--submenu-open')
        .forEach((element) => {
            element.classList.remove('navbar__item--submenu-open');
        });
    document.getElementById('body')?.classList.remove('no-scroll');
    navbar
        ?.querySelector('.navbar__overlay')
        ?.classList.remove('navbar__overlay--show');
    window.dispatchEvent(new CustomEvent('navbar-close-all'));
};

const NavigationItem = ({
    links = [],
    contentLink = null,
    values,
    menuDepth = 0,
    openDesktopSubmenuId = null,
    setOpenDesktopSubmenuId = () => {},
    forceTabIndex,
}) => {
    const menuRef = useRef(null);
    const menuOverlayRef = useRef(null);
    const openTriggerRef = useRef(null);
    const linkRef = useRef(null);
    const shouldFocusSubmenuRef = useRef(false);
    const shouldFocusMenuRef = useRef(false);
    const menuIdRef = useRef(null);
    if (!menuIdRef.current) {
        menuIdCounter += 1;
        menuIdRef.current = `navbar-menu-${menuIdCounter}`;
    }
    const menuId = menuIdRef.current;
    const [isOpen, setIsOpen] = useState(false);
    const [isDesktop, setIsDesktop] = useState(false);
    const isRoot = !contentLink;
    const isTopLevel = menuDepth === 1;
    const hasChildren = links.length > 0;
    const isDesktopSubmenuOpen =
        isTopLevel && openDesktopSubmenuId === menuId;
    const isPanelHidden = !isDesktop
        ? !isOpen
        : isTopLevel && !isDesktopSubmenuOpen;
    const childTabIndex = isPanelHidden ? -1 : forceTabIndex;
    const isHiddenFromTabOrder = forceTabIndex === -1;

    useEffect(() => {
        const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
        const updateViewport = () => setIsDesktop(mediaQuery.matches);
        updateViewport();
        mediaQuery.addEventListener('change', updateViewport);
        return () => mediaQuery.removeEventListener('change', updateViewport);
    }, []);

    useEffect(() => {
        const resetOpenState = () => setIsOpen(false);
        window.addEventListener('navbar-close-all', resetOpenState);
        return () =>
            window.removeEventListener('navbar-close-all', resetOpenState);
    }, []);

    useEffect(() => {
        if (!isTopLevel) {
            return;
        }

        const parent = linkRef.current?.closest('.navbar__item');
        parent?.classList.toggle(
            'navbar__item--submenu-open',
            isDesktopSubmenuOpen
        );
    }, [isTopLevel, isDesktopSubmenuOpen]);

    useEffect(() => {
        if (!isDesktopSubmenuOpen || !shouldFocusSubmenuRef.current) {
            return;
        }

        shouldFocusSubmenuRef.current = false;
        focusFirstMenuItem(menuRef.current);
    }, [isDesktopSubmenuOpen]);

    useEffect(() => {
        if (!isOpen || isDesktop || !shouldFocusMenuRef.current) {
            return;
        }

        shouldFocusMenuRef.current = false;
        focusFirstMenuItem(menuRef.current);
    }, [isOpen, isDesktop]);

    useEffect(() => {
        if (!isDesktopSubmenuOpen) {
            return;
        }

        const handleClickOutside = (event) => {
            const item = linkRef.current?.closest('.navbar__item');
            if (item?.contains(event.target)) {
                return;
            }
            setOpenDesktopSubmenuId(null);
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [isDesktopSubmenuOpen, setOpenDesktopSubmenuId]);

    const isMenuVisible = () =>
        menuRef.current?.classList.contains('navbar__menu--show');

    const openMenu = useCallback(() => {
        const parent = menuRef.current?.parentElement;
        const parentList = parent?.closest('ul');

        parent?.classList.add('navbar__menu--current');
        parentList?.classList.add('navbar__menu--behind');

        document.getElementById('body')?.classList.add('no-scroll');
        menuRef.current?.classList.add('navbar__menu--show');
        menuOverlayRef.current?.classList.add('navbar__overlay--show');
        shouldFocusMenuRef.current = true;
        setIsOpen(true);
    }, []);

    const closeMenu = useCallback(
        (returnFocus = true) => {
            const parent = menuRef.current?.parentElement;
            const parentList = parent?.closest('ul');

            parent?.classList.remove('navbar__menu--current');
            parentList?.classList.remove('navbar__menu--behind');

            if (isRoot) {
                closeAllNavbarMenus(menuRef.current?.closest('.navbar'));
            } else {
                menuRef.current?.classList.remove('navbar__menu--show');
            }

            menuOverlayRef.current?.classList.remove('navbar__overlay--show');
            setIsOpen(false);

            if (returnFocus) {
                openTriggerRef.current?.focus();
            }
        },
        [isRoot]
    );

    const openDesktopSubmenu = useCallback(() => {
        shouldFocusSubmenuRef.current = true;
        setOpenDesktopSubmenuId(menuId);
    }, [menuId, setOpenDesktopSubmenuId]);

    const closeDesktopSubmenu = useCallback(
        (returnFocus = true) => {
            setOpenDesktopSubmenuId(null);
            if (returnFocus) {
                linkRef.current?.focus();
            }
        },
        [setOpenDesktopSubmenuId]
    );

    const toggleMenu = useCallback(() => {
        if (isMenuVisible()) {
            closeMenu();
        } else {
            openMenu();
        }
    }, [closeMenu, openMenu]);

    const handleTopLevelLinkKeyDown = (event) => {
        if (!isDesktop || !hasChildren) {
            return;
        }

        if (event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            if (isDesktopSubmenuOpen) {
                closeDesktopSubmenu();
            } else {
                openDesktopSubmenu();
            }
        }
    };

    const handleTopLevelLinkBlur = (event) => {
        if (!isDesktop || !isDesktopSubmenuOpen) {
            return;
        }

        const item = linkRef.current?.closest('.navbar__item');
        if (item && event.relatedTarget && item.contains(event.relatedTarget)) {
            return;
        }

        closeDesktopSubmenu(false);
    };

    const handleMenuKeyDown = (event) => {
        if (event.key === 'Escape' && isDesktopSubmenuOpen) {
            event.preventDefault();
            closeDesktopSubmenu();
            return;
        }

        if (event.key !== 'Escape' || !isMenuVisible()) {
            return;
        }

        event.preventDefault();
        closeMenu();
    };

    const handleSubmenuFocusOut = (event) => {
        if (!isDesktop || !isDesktopSubmenuOpen) {
            return;
        }

        const item = menuRef.current?.closest('.navbar__item');
        if (item && event.relatedTarget && item.contains(event.relatedTarget)) {
            return;
        }

        closeDesktopSubmenu(false);
    };

    const getSubmenuLabel = () =>
        translate('navigation.open.submenu.label').replace(
            '{0}',
            stripHtml(contentLink?.name)
        );

    const additionClass =
        contentLink && contentLink.attributes
            ? contentLink.attributes.cssValue
            : null;
    const selectedClass =
        contentLink && contentLink.isSelected ? 'navbar__link--selected' : '';
    const hasChildrenClass = hasChildren ? 'has-children' : null;
    const hasNameOrChildren = (link) =>
        link.name || (link.links || []).length > 0;

    const linkClassName = `navbar__link ${selectedClass} ${
        hasChildrenClass || ''
    } ${additionClass || ''}`;

    const linkProps = {
        className: linkClassName,
        ref: linkRef,
        ...(isTopLevel && hasChildren
            ? {
                  'aria-haspopup': true,
                  'aria-expanded': isDesktopSubmenuOpen,
                  'aria-controls': menuId,
                  onKeyDown: handleTopLevelLinkKeyDown,
                  onBlur: handleTopLevelLinkBlur,
              }
            : {}),
        ...(forceTabIndex !== undefined ? { tabIndex: forceTabIndex } : {}),
    };

    return (
        <Fragment>
            {isRoot ? (
                <Fragment>
                    <button
                        type="button"
                        className="navbar__overlay"
                        ref={menuOverlayRef}
                        aria-label={translate('navigation.close.menu.label')}
                        onClick={() => closeMenu(false)}
                        tabIndex={-1}
                    ></button>
                    <button
                        type="button"
                        ref={openTriggerRef}
                        className="navbar__link--block navbar__icon--menu navbar__icon"
                        aria-label={translate('navigation.open.menu.label')}
                        aria-expanded={isOpen}
                        aria-controls={menuId}
                        onClick={toggleMenu}
                    ></button>
                </Fragment>
            ) : (
                <Fragment>
                    {contentLink.url ? (
                        <a
                            {...linkProps}
                            href={contentLink.url}
                            dangerouslySetInnerHTML={{
                                __html: contentLink.name,
                            }}
                        ></a>
                    ) : (
                        <span
                            {...linkProps}
                            dangerouslySetInnerHTML={{
                                __html: contentLink.name,
                            }}
                        ></span>
                    )}
                    {hasChildren && (
                        <button
                            type="button"
                            ref={openTriggerRef}
                            className="navbar__icon--caret-right navbar__icon navbar__icon--open"
                            aria-label={getSubmenuLabel()}
                            aria-expanded={isOpen}
                            aria-controls={menuId}
                            tabIndex={isHiddenFromTabOrder ? -1 : undefined}
                            onClick={toggleMenu}
                        ></button>
                    )}
                </Fragment>
            )}

            {hasChildren && (
                <ul
                    className="navbar__menu"
                    id={menuId}
                    ref={menuRef}
                    onKeyDown={handleMenuKeyDown}
                    onBlur={handleSubmenuFocusOut}
                    aria-hidden={isPanelHidden ? true : undefined}
                    tabIndex={isPanelHidden ? -1 : undefined}
                >
                    <li className="navbar__menu-header" tabIndex={isPanelHidden ? -1 : undefined}>
                        {isRoot ? (
                            <button
                                type="button"
                                className="navbar__icon navbar__icon--close"
                                aria-label={translate(
                                    'navigation.close.menu.label'
                                )}
                                tabIndex={isPanelHidden ? -1 : undefined}
                                onClick={toggleMenu}
                            ></button>
                        ) : (
                            <Fragment>
                                <button
                                    type="button"
                                    className="navbar__icon--caret-left navbar__icon"
                                    aria-label={translate(
                                        'navigation.back.submenu.label'
                                    )}
                                    tabIndex={isPanelHidden ? -1 : undefined}
                                    onClick={toggleMenu}
                                ></button>
                                <button
                                    type="button"
                                    className="navbar__title"
                                    tabIndex={isPanelHidden ? -1 : undefined}
                                    onClick={toggleMenu}
                                    dangerouslySetInnerHTML={{
                                        __html: contentLink.name,
                                    }}
                                ></button>
                            </Fragment>
                        )}
                    </li>
                    {links.map(
                        (link) =>
                            hasNameOrChildren(link) && (
                                <li className="navbar__item" key={link.url} tabIndex={isPanelHidden ? -1 : undefined}>
                                    <NavigationItem
                                        links={link.links}
                                        contentLink={link}
                                        values={values}
                                        menuDepth={menuDepth + 1}
                                        openDesktopSubmenuId={
                                            openDesktopSubmenuId
                                        }
                                        setOpenDesktopSubmenuId={
                                            setOpenDesktopSubmenuId
                                        }
                                        forceTabIndex={childTabIndex}
                                    />
                                </li>
                            )
                    )}
                    {values?.loginUrl && isRoot && (
                        <li className="navbar__item show-for-small-only" tabIndex={isPanelHidden ? -1 : undefined}>
                            <a
                                className="navbar__link navbar__link--login"
                                href={values.loginUrl}
                                rel="nofollow"
                                tabIndex={isPanelHidden ? -1 : undefined}
                            >
                                {values.loginText}
                            </a>
                        </li>
                    )}
                </ul>
            )}
        </Fragment>
    );
};

export default NavigationItem;
